import { BloodGlucoseObservationWithReading } from "@/model"
import cn from "@/util/cn"
import { DateTimeFormatter } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { ChatBubbleIcon, ChevronRightIcon, LockClosedIcon } from "@radix-ui/react-icons"
import {
  needsFeedback,
  readingValueBackgroundColorFor,
  readingValueBorderColorFor,
  readingValueTextColorFor,
  textForClassification,
} from "../dashboardData"

type LastReadingCardProps = {
  observation: BloodGlucoseObservationWithReading | null | undefined
  onClick?: (observation: BloodGlucoseObservationWithReading) => void
}

const HEADER_DATE = DateTimeFormatter.ofPattern("MMM dd, hh:mm a").withLocale(Locale.US)

const LastReadingCard = ({ observation, onClick }: LastReadingCardProps) => {
  if (!observation) {
    return null
  }

  const { reading, dateTime } = observation
  const feedback = needsFeedback(observation)
  const label = textForClassification(reading.classification)
  const style = feedback
    ? { backgroundColor: "#FEF9C3", borderColor: "#FFE21A" }
    : {
        backgroundColor: readingValueBackgroundColorFor(reading.classification),
        borderColor: readingValueBorderColorFor(reading.classification),
      }

  return (
    <div className="mt-6 flex flex-col gap-2">
      <p className="text-sm text-primary-contrast">
        <span className="font-bold">Last measurement</span>{" "}
        <span className="text-primary-contrast/70">{dateTime.format(HEADER_DATE)}</span>
      </p>
      <button
        type="button"
        onClick={() => onClick?.(observation)}
        className={cn("flex items-center justify-between rounded-lg border px-3 py-2 text-left")}
        style={style}
      >
        <div className="flex items-baseline gap-1">
          <span className="text-[38px] font-bold leading-none text-primary-contrast">
            {reading.bloodGlucoseInMilligramsPerDeciliter}
          </span>
          <span className="text-xs text-primary-contrast">mg/dL</span>
        </div>
        <span className="flex items-center gap-2">
          {!observation.isFormEditable && (
            <LockClosedIcon aria-label="Locked reading" className="h-4 w-4" />
          )}
          {feedback ? (
            <span className="flex items-center gap-1 rounded-md bg-[rgba(255,223,0,0.5)] px-2.5 py-1 text-sm italic text-primary-contrast">
              <ChatBubbleIcon aria-hidden className="h-3.5 w-3.5" />
              Needs feedback
            </span>
          ) : label ? (
            <span
              className="rounded-md px-2 py-1 text-sm font-semibold"
              style={{
                backgroundColor: readingValueBackgroundColorFor(reading.classification),
                color: readingValueTextColorFor(reading.classification),
              }}
            >
              {label}
            </span>
          ) : null}
          <ChevronRightIcon aria-hidden className="h-5 w-5 text-primary-contrast" />
        </span>
      </button>
    </div>
  )
}

export default LastReadingCard
